import React from "react";
import * as Mui from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Groups2Outlined,
  MoreVert,
} from "@mui/icons-material";
import { themes } from "services/constants";
import {
  designType,
  designShell,
  designColors,
  designFontFamily,
  designWeights,
  pageHeaderRow,
  rowsPerPageOptions,
  rowsPerPageControl as sharedRowsPerPageControl,
  paginationFooter as sharedPaginationFooter,
  statusChip as sharedStatusChip,
  FORM_ACTION_BUTTON_WIDTH,
  HEADER_ACTION_BUTTON_WIDTH,
  APP_BAR_OFFSET,
} from "services/constants/designTokens";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";

export const roleAndGroupUi = {
  page: {
    width: "100%",
    px: { xs: 1, md: 2 },
    pt: { xs: 1, md: 1.5 },
    pb: 3,
    ...designShell.pageSurface,
  },
  headerRow: {
    ...designShell.headerRowBase,
    alignItems: { xs: "flex-start", md: "center" },
    mb: 3,
  },
  title: {
    ...designType.pageTitle,
    fontFamily: designFontFamily,
    letterSpacing: "-0.01em",
  },
  subtitle: {
    ...designType.pageSubtitle,
    fontFamily: designFontFamily,
    lineHeight: 1.5,
  },
  sectionCard: designShell.sectionCard,
  // Matches Companies'/Users' own stickyCustomFilterBar/stickyFooterBar
  // (adminListScreenUi.js) -- CustomFilter's rendered height isn't fixed
  // (its chip row only appears once a filter/sort is applied), so the
  // sticky table header needs customFilterHeight (measured in UserRole/
  // UserGroup's own index.js) added to its `top` offset, or the header ends
  // up sitting under the filter chips instead of below them. The footer
  // similarly needs its own height reserved as TableContainer padding so
  // the sticky pagination bar doesn't cover the last row(s).
  stickyCustomFilterBar: {
    position: "sticky",
    top: APP_BAR_OFFSET,
    zIndex: 4,
    backgroundColor: "#FFFFFF",
  },
  stickyFooterBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 3,
    px: 2,
    py: 0.75,
    borderTop: "1px solid #E2E8F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 -10px 24px rgba(15, 23, 42, 0.06)",
  },
  formCard: {
    px: { xs: 2, md: 2.5 },
    py: { xs: 1.75, md: 2 },
  },
  formGrid: {
    alignItems: "flex-start",
  },
  formFieldWrap: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    height: "100%",
  },
  toolbarCard: {
    p: { xs: 2, md: 2.5 },
    mb: 2,
  },
  tableCard: designShell.tableCard,
  tabGroup: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    maxWidth: "100%",
    flex: "0 0 auto",
    gap: 1.25,
    p: 0.75,
    borderRadius: "16px",
    border: "1px solid #DDE6EE",
    backgroundColor: "#F8FAFC",
    minHeight: 56,
  },
  tabButton: (active) => ({
    minWidth: 138,
    px: 2.5,
    minHeight: 42,
    borderRadius: "12px",
    border: active ? "1px solid rgba(8, 145, 178, 0.16)" : "1px solid transparent",
    background: active
      ? "linear-gradient(135deg, #0F766E 0%, #0D9488 100%)"
      : "transparent",
    color: active ? "#FFFFFF" : "#0F172A",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    fontFamily: designFontFamily,
    textTransform: "none",
    boxShadow: active ? "0 10px 20px rgba(13, 148, 136, 0.2)" : "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    "&:hover": {
      background: active
        ? "linear-gradient(135deg, #0F766E 0%, #0D9488 100%)"
        : "#EEF6F5",
    },
    "& .MuiButton-startIcon": {
      margin: 0,
    },
  }),
  primaryButton: {
    ...designShell.primaryButtonBase,
    fontFamily: designFontFamily,
    minWidth: FORM_ACTION_BUTTON_WIDTH,
    "&.Mui-disabled": {
      color: "#94A3B8",
      background: "#E2E8F0",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    minWidth: FORM_ACTION_BUTTON_WIDTH,
    minHeight: 46,
    px: 2.5,
    borderRadius: "12px",
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: designFontFamily,
    color: "#0F172A",
    backgroundColor: "#FFFFFF",
    border: "1px solid #D7E0EA",
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#CBD5E1",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      minHeight: 48,
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#D7E0EA",
      },
      "&:hover fieldset": {
        borderColor: "#A5B4C7",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0D9488",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: 14,
      fontWeight: 500,
      fontFamily: designFontFamily,
      color: "#0F172A",
    },
    "& .MuiInputBase-input::placeholder": {
      fontFamily: designFontFamily,
      opacity: 1,
    },
  },
  panelTitle: designType.panelTitle,
  // Card-header bars inside a form ("Modules & Sub Modules", "Companies &
  // Role Assignment", etc.) — smaller than panelTitle (18px), which is
  // sized for an actual drawer/page panel title (see AddCompany.js), not a
  // sub-section heading nested inside one.
  sectionHeader: {
    fontSize: 16,
    fontWeight: 600,
    fontFamily: designFontFamily,
    color: designColors.slate800,
  },
  sectionNote: {
    mt: 0.5,
    fontSize: 14,
    fontFamily: designFontFamily,
    color: "#64748B",
  },
  // position/zIndex layered on top of the base cell — matches adminUi/
  // companyUi's tableHeadCell (adminListScreenUi.js), which is what keeps
  // Users'/Companies' table header pinned under the app bar while scrolling.
  // `top` itself is supplied per screen (see UserGroup/UserRole index.js).
  // py explicit — without it these cells fall back to MUI's default ~16px
  // TableCell padding (no size="small" on this table), rendering visibly
  // taller than intended and, combined with the header's slate100 fill
  // sitting right under the pageBg gap above the card, reading as one large
  // empty band before the header text. adminUi/companyUi's tableHeadCell
  // (adminListScreenUi.js) sets the same compact py for the same reason.
  tableHeadCell: {
    ...designShell.tableHeadCellBase,
    fontFamily: designFontFamily,
    py: 0.75,
    position: "sticky",
    zIndex: 2,
  },
  tableBodyCell: {
    py: 2,
    fontSize: 14,
    fontFamily: designFontFamily,
    color: "#0F172A",
    borderBottom: "1px solid #EDF2F7",
    verticalAlign: "top",
  },
  cardHeaderBar: {
    backgroundColor: "#F8FAFC",
    px: 2,
    py: 1.75,
    borderBottom: "1px solid #E2E8F0",
  },
  cardHeaderTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  },
  infoIcon: {
    fontSize: 18,
    color: designColors.slate500,
  },
  helperText: {
    fontSize: 13,
    fontFamily: designFontFamily,
    color: designColors.slate600,
  },
  treeProductName: {
    fontSize: 15,
    fontWeight: designWeights.heading,
    fontFamily: designFontFamily,
    color: designColors.slate800,
  },
  treeProductMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    justifySelf: "end",
  },
  treeSelectionMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    minWidth: 20,
    flex: "0 0 20px",
  },
  treeLabelRow: {
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "24px minmax(0, 1fr)",
    minWidth: 0,
    columnGap: 8,
  },
  treeIconWrap: {
    width: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: designColors.slate700,
    flex: "0 0 auto",
  },
  treeCountBadge: {
    minWidth: 46,
    height: 28,
    px: 1,
    borderRadius: "999px",
    border: `1px solid ${designColors.slate200}`,
    backgroundColor: designColors.white,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: designFontFamily,
    color: designColors.slate600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
  },
  treeModuleName: {
    fontSize: 14,
    fontWeight: designWeights.semibold,
    fontFamily: designFontFamily,
    color: designColors.slate800,
    lineHeight: 1.3,
    minWidth: 0,
    flex: 1,
  },
  // columnGap is a little roomier here (12px) than the module/submodule
  // rows below (10px) — this is the tree's top-level "main title" row, so
  // its bolder, larger treeProductName reads better with slightly more
  // breathing room next to its checkbox than the smaller child rows need.
  treeProductRow: {
    display: "grid",
    gridTemplateColumns: "20px 20px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: 1.5,
    px: 1.25,
    py: 1,
    cursor: "pointer",
  },
  // Indented one tree level (24px) in from treeProductRow's own 10px (px:
  // 1.25) inset — pl replaces the old symmetric `px` so the row's checkbox
  // column lands under a genuinely nested position instead of directly
  // below the product row's own checkbox (see treeConnectorList below for
  // how the submodule level continues the same 24px-per-level staircase).
  treeModuleRow: {
    borderRadius: "10px",
    pl: 3.5,
    pr: 0.5,
    py: 0.375,
    minHeight: 34,
    display: "grid",
    gridTemplateColumns: "20px 20px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: 1.25,
    "&:hover": { backgroundColor: "#F8FAFC" },
  },
  // First column (20px) mirrors treeModuleRow's expand-icon width so the
  // checkbox column that follows lines up on the same staircase even
  // though submodules have no expand icon of their own.
  treeSubmoduleRow: {
    py: 0.2,
    minHeight: 30,
    display: "grid",
    gridTemplateColumns: "20px 20px minmax(0, 1fr)",
    alignItems: "center",
    columnGap: 1.25,
  },
  treeSubmoduleSpacer: {
    width: 20,
    height: 20,
  },
  // Continues treeModuleRow's indent by one more 24px step (ml 34px lands
  // the connector line under the module row's own inset, pl 18px offsets
  // the submodule content from that line) so submodule checkboxes align
  // under module checkboxes exactly the way module checkboxes align under
  // product checkboxes.
  treeConnectorList: {
    ml: "34px",
    pl: "18px",
    pb: 0.25,
    borderLeft: `1px dashed ${designColors.slate200}`,
  },
  treeSubmoduleName: {
    fontSize: 13,
    fontWeight: designWeights.medium,
    fontFamily: designFontFamily,
    color: designColors.slate700,
    lineHeight: 1.35,
    minWidth: 0,
    flex: 1,
  },
  panelAlert: {
    m: 2,
    "& .MuiAlert-message": {
      fontFamily: designFontFamily,
      fontSize: 13.5,
    },
  },
  permissionSummaryTitle: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: designFontFamily,
    color: designColors.tealDark,
  },
  permissionSummaryNote: {
    mt: 0.25,
    fontSize: 12,
    fontFamily: designFontFamily,
    color: designColors.slate600,
  },
  permissionSummaryBar: {
    mx: 1,
    mt: 1,
    mb: 0.5,
    px: 1.5,
    py: 1,
    borderRadius: "10px",
    backgroundColor: "#EDF9F7",
    border: "1px solid #D7EFEB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    flexWrap: "wrap",
  },
  inlineActionButton: {
    minHeight: 34,
    px: 1.25,
    borderRadius: "10px",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: designFontFamily,
    color: designColors.slate800,
    backgroundColor: designColors.white,
    border: "1px solid #D7E0EA",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#CBD5E1",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    },
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
  },
  permissionTableHeadCell: {
    ...designShell.tableHeadCellBase,
    fontFamily: designFontFamily,
    fontWeight: 700,
    fontSize: 13,
    textAlign: "center",
    py: 1.1,
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  permissionModuleCell: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: designFontFamily,
    color: designColors.tealDark,
    backgroundColor: "#F2FBFA",
    borderBottom: "1px solid #D7EFEB",
    py: 1,
    pl: 2,
    pr: 2,
  },
  permissionTableBodyCell: {
    fontSize: 13.5,
    fontWeight: 500,
    fontFamily: designFontFamily,
    color: designColors.slate800,
    textAlign: "center",
    py: 0.75,
    px: 1.5,
    borderBottom: "1px solid #EDF2F7",
  },
  permissionTableRow: {
    "& .MuiTableCell-root": {
      py: 0.75,
      px: 1.5,
    },
  },
  addModuleButton: {
    minHeight: 46,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    fontFamily: designFontFamily,
    borderColor: "#7DD3C7",
    color: "#0F766E",
    "&:hover": {
      borderColor: "#14B8A6",
      backgroundColor: "#F0FDFA",
    },
  },
  stickyFooter: {
    mt: 3,
    px: { xs: 2, md: 3 },
    py: 2,
    position: "sticky",
    bottom: 0,
    backgroundColor: "#FFFFFF",
    borderTop: "1px solid #E2E8F0",
    zIndex: 5,
  },
  companyIcon: {
    width: 40,
    height: 40,
    bgcolor: "#E6F8F4",
    color: "#0F766E",
  },
  summaryCard: {
    p: 2.5,
    minWidth: 280,
    height: "100%",
  },
  summaryLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#94A3B8",
    mb: 0.75,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: 600,
    color: "#0F172A",
    mb: 2,
  },
  iconButton: {
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
    color: "#475569",
  },
  dangerIconButton: {
    borderRadius: "12px",
    border: "1px solid #FECACA",
    backgroundColor: "#FEF2F2",
    color: "#DC2626",
    "&:hover": {
      backgroundColor: "#FEE2E2",
      borderColor: "#FCA5A5",
    },
  },
  noteBanner: {
    mt: 2,
    px: 2,
    py: 1.5,
    borderRadius: "14px",
    border: "1px solid #CBE7F0",
    backgroundColor: "#F0FBFF",
    color: "#335C67",
    fontSize: 14,
  },
  rowsPerPageSelect: designShell.rowsPerPageSelect,
  paginationIconButton: designShell.paginationIconButton,
};

export const statusChip = sharedStatusChip;

export const listHeaderActions = ({
  title,
  subtitle,
  activeTab,
  onTabChange,
  primaryLabel,
  onPrimaryClick,
  showPrimaryAction,
  showRolesTab = true,
  showUserGroupTab = true,
}) =>
  pageHeaderRow({
    title,
    subtitle,
    primaryLabel,
    onPrimaryClick,
    primaryButtonSx: { minWidth: HEADER_ACTION_BUTTON_WIDTH, alignSelf: "stretch" },
    sx: roleAndGroupUi.headerRow,
    showPrimaryAction,
    extraContent: (
      <Mui.Box sx={roleAndGroupUi.tabGroup}>
        {showRolesTab ? (
          <Mui.Button
            startIcon={<CheckCircle fontSize="small" />}
            sx={roleAndGroupUi.tabButton(activeTab === "Roles")}
            onClick={() => onTabChange("Roles")}
          >
            Roles
          </Mui.Button>
        ) : null}
        {showUserGroupTab ? (
          <Mui.Button
            startIcon={<Groups2Outlined fontSize="small" />}
            sx={roleAndGroupUi.tabButton(activeTab === "User Group")}
            onClick={() => onTabChange("User Group")}
          >
            User Groups
          </Mui.Button>
        ) : null}
      </Mui.Box>
    ),
  });

export const rowsPerPageControl = (args) => sharedRowsPerPageControl(args);

export const paginationFooter = ({ rowsPerPageOptions: options = rowsPerPageOptions, ...rest }) =>
  sharedPaginationFooter({ ...rest, rowsPerPageOptions: options });

// stopPropagation so opening the "..." menu doesn't also fire the row's own
// onClick (rows navigate straight to the edit screen — see UserGroup/UserRole
// index.js), the same way Users/Companies guard their row-action buttons.
export const actionMenuButton = (onClick) => (
  <Mui.IconButton
    sx={roleAndGroupUi.iconButton}
    onClick={(event) => {
      event.stopPropagation();
      onClick(event);
    }}
  >
    <MoreVert fontSize="small" />
  </Mui.IconButton>
);

export const menuItemIconSx = { color: themes.headLine, fontSize: 18 };

export const duplicateMenuItem = (
  <Mui.MenuItem disabled>
    <ContentCopy sx={menuItemIconSx} />
    <Mui.ListItemText sx={{ ml: 1 }} primary="Duplicate" />
  </Mui.MenuItem>
);

// `section` is the list screen this form was reached from ("Roles" or "User
// Groups" — see RoleAndUserGroup/index.js's own tab-driven title) and forms
// the clickable middle breadcrumb segment; `onSectionClick` is what it
// navigates to — the same place the old "‹ Back" icon went. Exiting the form
// via Cancel further down still works exactly as before.
export const formHeader = ({ title, subtitle, section, onSectionClick, action }) => (
  <Mui.Stack spacing={2.5} sx={{ mb: 3 }}>
    <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
      <Mui.Box>
        <GlobalBreadcrumb
          trail={[
            "Administration",
            { label: section, onClick: onSectionClick },
            title,
          ]}
        />
        <Mui.Typography sx={roleAndGroupUi.title}>{title}</Mui.Typography>
        <Mui.Typography sx={roleAndGroupUi.subtitle}>{subtitle}</Mui.Typography>
      </Mui.Box>
      {action}
    </Mui.Stack>
  </Mui.Stack>
);
